import { useEffect, useMemo, useState } from 'react'

import classNames from 'classnames'
import styled from 'styled-components'
import { EditableContent } from 'shared/ui'
import { TagsSelector } from 'shared/ui/TagsSelector'

type StatusItem = {
  label: string
  key: string
  color: string
}

type SystemTag = { id: string; value: string; label?: string }

interface PublicBoardHeaderProps {
  // Data
  title?: string
  description?: string
  boardTags?: string[]
  systemTags?: SystemTag[]
  status?: StatusItem | null
  statuses?: StatusItem[]

  // Handlers (external)
  onTitleChange?: (value: string) => void
  onDescriptionChange?: (value: string) => void
  onTagsChange?: (ids: string[]) => void
  onCreateTag?: (value: string) => void
  onStatusChange?: (status: StatusItem) => void

  // Visibility toggles
  showTitle?: boolean
  showDescription?: boolean
  showTags?: boolean
  showStatus?: boolean

  // Disable editing per section
  disableTitle?: boolean
  disableDescription?: boolean
  disableTags?: boolean
  disableStatus?: boolean
  tagsSelectorMode?: 'select-create' | 'select-only'

  // Misc
  className?: string
  withoutUserInteraction?: boolean
}

export const PublicBoardHeader = ({
  title = '',
  description = '',
  boardTags = [],
  systemTags = [],

  onTitleChange,
  onDescriptionChange,
  onTagsChange,
  onCreateTag,

  showTitle = true,
  showDescription = true,
  showTags = true,

  disableTitle = false,
  disableDescription = false,
  disableTags = false,
  tagsSelectorMode = 'select-create',

  className,
  withoutUserInteraction,
}: PublicBoardHeaderProps) => {
  const [titleValue, setTitleValue] = useState<string>(title)
  const [descriptionValue, setDescriptionValue] = useState<string>(description)

  useEffect(() => {
    setTitleValue(title)
  }, [title])

  useEffect(() => {
    setDescriptionValue(description)
  }, [description])

  const systemTagsList = useMemo(() => {
    return systemTags
      .map((t) => ({ label: t.label ?? t.value, value: t.id, id: t.id }))
      .filter((t) => !boardTags.includes(t.id))
  }, [systemTags, boardTags])

  const boardTagsList = useMemo(() => {
    return boardTags.map((tagId) => ({
      id: tagId,
      value: tagId,
      label: systemTags.find((t) => t.id === tagId)?.label ?? systemTags.find((t) => t.id === tagId)?.value ?? tagId,
    }))
  }, [boardTags, systemTags])

  const handleTitleBlur = (value: string) => {
    onTitleChange?.(value)
  }

  const handleDescriptionBlur = (value: string) => {
    onDescriptionChange?.(value)
  }

  const handleTagsSelect = (ids: string[]) => {
    onTagsChange?.(ids)
  }

  const handleTagsChange = (ids: string[]) => {
    onTagsChange?.(ids)
  }

  const handleCreateTag = (value: string) => {
    onCreateTag?.(value)
  }

  return (
    <SContainer
      className={classNames('public-board-header', className, {
        'without-user-interaction': withoutUserInteraction,
      })}
    >

      {showTitle && (
        <STitleEditable
          value={titleValue}
          onChange={setTitleValue}
          onBlur={handleTitleBlur}
          placeholder="Enter board title"
          disabled={disableTitle}
          maxLines={3}
          singleLine={false}
        />
      )}

      {showDescription && (
        <SDescriptionEditable
          value={descriptionValue}
          onChange={setDescriptionValue}
          onBlur={handleDescriptionBlur}
          placeholder="Click to add description"
          placeholderColor="rgba(0, 0, 0, 0.45)"
          disabled={disableDescription}
        />
      )}

      {showTags && (
        <STagsSelector
          className="board-tags-selector"
          classNameTagsContainer="board-tags-selector-container"
          options={systemTagsList}
          value={boardTagsList}
          onSelect={handleTagsSelect}
          onChange={handleTagsChange}
          onCreateTag={handleCreateTag}
          disabled={disableTags}
          showBorder={false}
          mode={tagsSelectorMode}
          isPublicLink={true}
        />
      )}
    </SContainer>
  )
}

const SContainer = styled.div`
  max-width: 600px;

  .ant-btn {
    margin-top: 32px;
  }
  
  &.without-user-interaction {
    pointer-events: none;
    cursor: default;
  }
`

// const SStatusRow = styled.div`
//   height: 20px;
//   margin-bottom: 16px;
//   display: flex;
//   align-items: center;
//   gap: 14px;
//   width: fit-content;
// `

const STagsSelector = styled(TagsSelector)`
  margin-top: 32px;
  border: 1px solid transparent;

  &.board-tags-selector-container {
    margin-left: -12px;
    margin-top: 26px;
    padding: 9px 16px;
    border-radius: 4px;
  }

  &.board-tags-selector {
    .ant-select-selector {
      &:hover {
        border-radius: 6px !important;
        cursor: pointer !important;
      }
    }
  }
`

const SDescriptionEditable = styled(EditableContent)`
  margin-top: 20px;
  margin-left: -10px;
  max-width: 900px;

  &:not([contenteditable='true']) {
    border-color: transparent;
    box-shadow: none;
    cursor: pointer;
  }
`

const STitleEditable = styled(EditableContent)`
  margin-top: -4px;
  margin-bottom: 4px;
  padding: 0 8px;
  max-width: 100%;
  margin-left: -8px;

  &:not([contenteditable='true']) {
    border-color: transparent;
    box-shadow: none;
    cursor: pointer;
  }

  font-size: 32px;
  font-weight: 600;
  line-height: 1.5;
  letter-spacing: 0.02em;
  color: rgba(0, 0, 0, 0.88);

  &:focus {
    border-color: var(--color-primary);
    box-shadow: 0 0 0 2px rgba(135, 71, 223, 0.1);
  }
`

export default PublicBoardHeader


